'use client';

import { faArrowDown, faArrowUp } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Checkbox } from '@pickleballinc/ui/components/ui/checkbox';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';

import CheckboxForm from '@/components/atoms/CheckboxForm';
import { ActionButtons } from '@/components/molecules';
import { RotationPlayerList } from '@/components/molecules/tie-breaker/RotationPlayerList';
import { LineupPlayerOption } from '@/entities';
import { PermissionRole } from '@/entities/PermissionRoles';
import { RotationPlayerInfo } from '@/entities/rotations/RotationsInfo';
import { useTeamLeagueInfo } from '@/hooks/custom-hooks/useTeamLeagueInfo';
import { useTeamEditParams } from '@/hooks/useTeamEditParams';
import { isPlayerDisabled, PlayerGender, reorderArrayItem, sortTieBreakerSinglesPlayers } from '@/infrastructure/utils/tieBreakerHelpers';
import { usePermissionStore } from '@/store/permission-store';
import { formatTeamLeagueLabels, getPlayerIneligibleReason, isPlayerIneligible } from '@/utils/helpers';

interface TieBreakerFormSinglesProps {
	teamTitle: string;
	players: LineupPlayerOption[];
	onCloseStep: () => void;
	onSave?: (selectedOrderedPlayerUuids: string[], team: number, deletedRotations?: { rotationUuid: string; playerOneUuid: string }[]) => void;
	isAwayTeam: boolean;
	groupTitle: string;
	rotationsInfo?: RotationPlayerInfo[];
	isCreatingTeamRotation: boolean;
	tieBreakerMatchUuid: string;
	canCaptainEditLineups: boolean;
}

interface TieBreakerFormSinglesValues {
	players: Record<string, boolean>;
}

export enum TieBreakerStep {
	SELECT = 'select',
	ORDER = 'order',
	SAVE = 'save'
}

export enum MoveDirection {
	UP = 'up',
	DOWN = 'down'
}

export const TieBreakerFormSingles = ({
	teamTitle,
	players,
	onCloseStep,
	onSave,
	isAwayTeam,
	groupTitle,
	rotationsInfo,
	isCreatingTeamRotation,
	tieBreakerMatchUuid,
	canCaptainEditLineups
}: TieBreakerFormSinglesProps) => {
	const [step, setStep] = useState<TieBreakerStep>(TieBreakerStep.SELECT);
	const [orderedPlayers, setOrderedPlayers] = useState<LineupPlayerOption[]>([]);
	const [selectAll, setSelectAll] = useState(true);
	const [_, setInitialRotatedUuids] = useState<string[]>([]);
	const teamLeagueInfo = useTeamLeagueInfo();
	const labels = formatTeamLeagueLabels(teamLeagueInfo);

	// A player can't be selected when the match gender filter excludes them, or when they're ineligible /
	// have responded "No" to availability.
	const isSelectionDisabled = useCallback(
		(player: LineupPlayerOption) => isPlayerDisabled(player.gender as PlayerGender, groupTitle) || isPlayerIneligible(player),
		[groupTitle]
	);

	const { isEditMode, isEditingHomeTeam, isEditingAwayTeam } = useTeamEditParams();
	const permissionsStore = usePermissionStore();

	const gamedayAccessRole = permissionsStore.gamedayAccess;

	const isUserCaptain = permissionsStore.userPermission === PermissionRole.TEAM_CAPTAIN;

	const canEditLineup = gamedayAccessRole || (isUserCaptain && canCaptainEditLineups);
	const canSetLineup = gamedayAccessRole || (isUserCaptain && canCaptainEditLineups);

	const form = useForm<TieBreakerFormSinglesValues>({
		defaultValues: {
			players: {}
		}
	});

	const { handleSubmit, setValue, watch, reset } = form;
	const watchedPlayers = watch('players');
	const selectedPlayerUuids = Object.entries(watchedPlayers || {})
		.filter(([_, isSelected]) => isSelected)
		.map(([uuid]) => uuid);

	useEffect(() => {
		if (!isEditMode) return;

		const isCorrectTeam = (isAwayTeam && isEditingAwayTeam) || (!isAwayTeam && isEditingHomeTeam);
		if (!isCorrectTeam) return;

		if (Array.isArray(rotationsInfo) && rotationsInfo.length > 0) {
			const formValues: Record<string, boolean> = {};

			players.forEach((player) => {
				const isDisabled = isSelectionDisabled(player);
				const isSelected = rotationsInfo.some((rotation) => rotation.playerOneUuid === player.userUuid);

				formValues[player.userUuid] = isDisabled ? false : isSelected;
			});

			reset({ players: formValues });

			const selectablePlayers = players.filter((p) => !isSelectionDisabled(p));
			const allSelected = selectablePlayers.every((p) => rotationsInfo.some((r) => r.playerOneUuid === p.userUuid));

			setSelectAll(allSelected);

			const ordered = rotationsInfo
				.map((rotation) => players.find((player) => player.userUuid === rotation.playerOneUuid))
				.filter((player): player is LineupPlayerOption => player !== undefined);

			const uuids = ordered.map((p) => p.userUuid);

			setInitialRotatedUuids(uuids);
			setOrderedPlayers(ordered);
			setStep(TieBreakerStep.SELECT);
		}
	}, [isEditMode, rotationsInfo, players, isSelectionDisabled, reset, isAwayTeam, isEditingAwayTeam, isEditingHomeTeam]);

	useEffect(() => {
		const values = Object.values(watchedPlayers || {});
		if (values.length) {
			const allSelected = values.every((val) => val === true);
			setSelectAll(allSelected);
		}
	}, [watchedPlayers]);

	const displayPlayers = useMemo(() => {
		if (!isEditMode) return players;

		return sortTieBreakerSinglesPlayers(players, rotationsInfo, selectedPlayerUuids);
	}, [isEditMode, players, rotationsInfo, selectedPlayerUuids]);

	const handleToggleSelectAll = () => {
		const newValue = !selectAll;
		players.forEach((p) => {
			if (p.gender && !isSelectionDisabled(p)) {
				setValue(`players.${p.userUuid}`, newValue);
			}
		});
		setSelectAll(newValue);
	};

	const handleNextStep = () => {
		const selected = selectedPlayerUuids.map((uuid) => players.find((p) => p.userUuid === uuid)).filter(Boolean) as LineupPlayerOption[];

		const orderedFromRotations = (rotationsInfo ?? [])
			.map((rotation) => selected.find((p) => p.userUuid === rotation.playerOneUuid))
			.filter((player): player is LineupPlayerOption => player !== undefined);

		const remaining = selected.filter((p) => !orderedFromRotations.find((op) => op.userUuid === p.userUuid));

		const fullOrder = [...orderedFromRotations, ...remaining];

		setOrderedPlayers(fullOrder);
		setStep(TieBreakerStep.ORDER);
	};

	const handleMove = (index: number, direction: MoveDirection) => {
		setOrderedPlayers((prev) => reorderArrayItem(prev, index, direction));
	};

	const handleFinalSubmit = () => {
		const orderedUuids = orderedPlayers.map((p) => p.userUuid);

		const deletedRotations = (rotationsInfo ?? [])
			.filter((rotation) => !orderedUuids.includes(rotation.playerOneUuid))
			.map((rotation) => ({
				rotationUuid: rotation.rotationUuid,
				playerOneUuid: rotation.playerOneUuid,
				playerTwoUuid: rotation.playerTwoUuid ?? null
			}));

		onSave?.(orderedUuids, isAwayTeam ? 2 : 1, deletedRotations);
		reset();
	};

	if (isEditMode && !canEditLineup) {
		return 'You are not allowed to edit team’s lineup.';
	}

	if (!isEditMode && !canSetLineup) {
		return 'You are not allowed to set the team’s lineup.';
	}

	const actionButtons = (backStep: TieBreakerStep, submitButtonText: string, onSubmit: () => void, submitDisabled = false) => {
		return (
			<ActionButtons
				className="w-full pt-10"
				submitButtonClassName="w-fit"
				closeButtonClassName="!w-fit w-full"
				onClose={() => (step === TieBreakerStep.SELECT ? onCloseStep() : setStep(backStep))}
				onSubmit={onSubmit}
				submitButtonText={submitButtonText}
				closeButtonText="Cancel"
				submitButtonDisabled={submitDisabled}
			/>
		);
	};

	const renderTeamTitle = () => {
		return (
			<p className="text-2xl pb-4 font-medium text-blue-500">
				{teamTitle} <span className="text-lg font-medium text-gray-900">({isAwayTeam ? 'A' : 'H'})</span>
			</p>
		);
	};

	const renderSelectedPlayers = () => {
		return (
			<RotationPlayerList
				title={renderTeamTitle()}
				rotations={rotationsInfo ?? []}
				showBothPlayers={false}
				teamTitle={teamTitle}
				tieBreakerMatchUuid={tieBreakerMatchUuid}
			/>
		);
	};

	// === Select players ===
	const renderSelectStep = () => {
		const selectablePlayers = players.filter((p) => !isSelectionDisabled(p));
		const isAllSelected = selectablePlayers.length > 0 && selectedPlayerUuids.length === selectablePlayers.length;

		const atLeastOneSelected = selectablePlayers.some((p) => selectedPlayerUuids.includes(p.userUuid));
		return (
			<>
				<div className="flex p-9 flex-col rounded-3xl border min-h-[450px] border-gray-300 max-h-[450px] h-full w-full max-w-[425px] bg-white">
					{renderTeamTitle()}
					<p className="text-gray-600">Select players for the tie {labels.tieBreakerMatchTitle}</p>
					<FormProvider {...form}>
						<form onSubmit={handleSubmit(handleNextStep)}>
							<div className="flex items-center gap-2 py-4 cursor-pointer">
								<Checkbox
									className="size-5 cursor-pointer rounded-md data-[state=checked]:bg-primary-100 data-[state=checked]:text-primary"
									id="selectAll"
									checked={isAllSelected}
									onCheckedChange={handleToggleSelectAll}
								/>
								<p className="text-base font-medium text-gray-900 cursor-default">Select all</p>
							</div>

							<hr className="mb-4" />

							<div className="flex flex-col gap-3 max-h-[240px] overflow-y-auto pr-2">
								{displayPlayers.map((player) => (
									<div key={player.userUuid} className="flex items-center gap-2 py-1 pl-1">
										<CheckboxForm
											name={`players.${player.userUuid}`}
											label={player.fullName}
											isDisabled={isSelectionDisabled(player)}
											tooltip={getPlayerIneligibleReason(player)}
										/>
									</div>
								))}
							</div>
						</form>
					</FormProvider>
				</div>

				{actionButtons(TieBreakerStep.SELECT, 'Next', handleSubmit(handleNextStep), !atLeastOneSelected)}
			</>
		);
	};

	// === Order players ===
	const renderOrderStep = () => (
		<>
			<div className="flex p-9 flex-col h-[450px] overflow-y-auto  rounded-3xl border border-gray-300 w-full max-w-[425px] bg-white">
				{renderTeamTitle()}
				<p className="font-medium mb-9 text-gray-600">You can change the order by clicking the arrows to move player up or down.</p>

				<div className="flex flex-col gap-4">
					{orderedPlayers.map((player, index) => (
						<div key={player.userUuid} className="flex items-center gap-5">
							<div className="flex items-center gap-3 w-16">
								<span className="w-6 h-6 flex items-center justify-center aspect-square rounded-full border border-gray-400 text-sm">
									{index + 1}
								</span>
								<div className="flex gap-3">
									{index > 0 && (
										<button type="button" onClick={() => handleMove(index, MoveDirection.UP)}>
											<FontAwesomeIcon icon={faArrowUp} size="sm" />
										</button>
									)}
									{index < orderedPlayers.length - 1 && (
										<button type="button" onClick={() => handleMove(index, MoveDirection.DOWN)}>
											<FontAwesomeIcon icon={faArrowDown} size="sm" />
										</button>
									)}
								</div>
							</div>

							<span className="flex-1 break-words">{player.fullName}</span>
						</div>
					))}
				</div>
			</div>

			{actionButtons(TieBreakerStep.SELECT, 'Next', () => setStep(TieBreakerStep.SAVE))}
		</>
	);

	// === Save players ===
	const renderSaveStep = () => (
		<>
			<div className="flex p-9 flex-col w-[420px] max-w-full h-[450px] overflow-y-auto rounded-3xl border border-gray-300 bg-white">
				{renderTeamTitle()}

				<p onClick={() => setStep(TieBreakerStep.SELECT)} className="text-darkorange-400 font-semibold cursor-pointer pb-4">
					Edit
				</p>

				<div className="flex flex-col gap-4">
					{orderedPlayers.map((player, index) => (
						<div key={player.userUuid} className="flex items-center gap-3">
							<span className="w-6 h-6 flex items-center justify-center aspect-square rounded-full border border-gray-400 text-sm">
								{index + 1}
							</span>
							<span className="break-words">{player.fullName}</span>
						</div>
					))}
				</div>
			</div>

			{actionButtons(TieBreakerStep.ORDER, 'Save', handleFinalSubmit, isCreatingTeamRotation)}
		</>
	);

	// === STEP: SELECT ===

	if (!isEditMode && rotationsInfo?.length !== 0) {
		return renderSelectedPlayers();
	}

	return {
		[TieBreakerStep.SELECT]: renderSelectStep,
		[TieBreakerStep.ORDER]: renderOrderStep,
		[TieBreakerStep.SAVE]: renderSaveStep
	}[step]();
};
